import { useCallback, useState } from 'react';

import {
  BodyIcon,
  DriveIcon,
  EngineIcon,
  FuelIcon,
  SeatIcon,
  TransmissionIcon,
  VehicleDetailIcon,
} from '@components/common/Icons';
import { Vehicle } from 'models/Vehicle';

import formatToDecimal from 'utils/formatToDecimal';

import TabsWithContentCard from '../TabsWithContentCard/TabsWithContentCard';

interface SpecificationsSectionProps {
  vehicle: Pick<
    Vehicle,
    | 'body_type'
    | 'stock_no'
    | 'fuel_type'
    | 'transmission_type'
    | 'engine_size'
    | 'seat_count'
    | 'drive_type'
    | 'glass_features'
    | 'glass_data'
    | 'odometer'
  >;
}

interface GlassDataProps {
  fuelUrban: string;
  towingCapacity: string;
}

const SpecificationsSection = ({ vehicle }: SpecificationsSectionProps) => {
  const [selectedTabIndex, setSelectedTabIndex] = useState<number>(0);

  const handleOnTabChange = useCallback(value => {
    setSelectedTabIndex(value);
  }, []);

  const vehicleDetailIcons = [
    {
      label: vehicle.body_type,
      icon: (
        <BodyIcon className="h-8 md:h-9 lg:h-12 w-8 md:w-9 lg:w-12 text-coral-100" />
      ),
    },
    {
      label: vehicle.fuel_type?.split('-')[0],
      icon: (
        <FuelIcon className="h-7 md:h-8 lg:h-9 w-7 md:w-8 lg:w-9 text-coral-100 ml-1" />
      ),
    },
    {
      label: vehicle.transmission_type,
      icon: (
        <TransmissionIcon className="h-6 md:h-7 lg:h-8 w-6 md:w-7 lg:w-8 text-coral-100" />
      ),
    },
    {
      label: `${vehicle.engine_size}L`,
      icon: (
        <EngineIcon className="h-7 md:h-8 lg:h-9 w-7 md:w-8 lg:w-9 text-coral-100" />
      ),
    },
    {
      label: vehicle.seat_count ? `${vehicle.seat_count} Seats` : '',
      icon: (
        <SeatIcon className="h-6 md:h-7 lg:h-8 w-6 md:w-7 lg:w-8 text-coral-100" />
      ),
    },
    {
      label: vehicle.drive_type,
      icon: (
        <DriveIcon className="h-7 md:h-8 lg:h-9 w-7 md:w-8 lg:w-9 text-coral-100" />
      ),
    },
  ];

  const glassFeatures = vehicle.glass_features as string[] | undefined;
  const glassFeaturesSorted = glassFeatures?.sort();
  const glassData = vehicle.glass_data as GlassDataProps | undefined;

  const specs = [
    ...(glassData?.fuelUrban
      ? [
          {
            label: `Fuel Consumption: ${glassData.fuelUrban}l / 100km`,
          },
        ]
      : []),
    ...(vehicle.odometer
      ? [
          {
            label: `Odometer: ${formatToDecimal(vehicle.odometer)}km `,
          },
        ]
      : []),
    ...(glassData?.towingCapacity
      ? [
          {
            label: `Towing Capacity: ${formatToDecimal(
              Number(glassData.towingCapacity),
            )}kg`,
          },
        ]
      : []),
  ];

  return (
    <>
      <TabsWithContentCard
        btnProps={{
          className: 'uppercase',
        }}
        tabs={[
          {
            label: 'OVERVIEW',
            component: (
              <>
                <div className="grid grid-cols-3 md:grid-cols-6">
                  {vehicleDetailIcons.map(({ label, icon }) => (
                    <VehicleDetailIcon
                      key={label}
                      label={label}
                      icon={icon}
                      className="mx-6 mb-4"
                    />
                  ))}
                </div>
                <div className="container flex flex-col flex-col-1 md:flex-row flex-row-3	md:space-x-3 lg:place-content-around">
                  {specs.map(spec => {
                    return (
                      <p
                        key={spec.label}
                        className="mt-4 text-dark-blue-100 text-sm leading-8 text-center lg:text-justify h-8 flex mx-[2px] lg:mx-0 px-5 items-center justify-center bg-blue-5 rounded-full flex-shrink-0 min-w-[111px]"
                      >
                        {spec.label}
                      </p>
                    );
                  })}
                </div>
                <div className="flex flex-col items-end mt-3">
                  <span className="text-grey-100 font-light text-xs ml-[46rem] block mb-1">{`#${vehicle.stock_no}`}</span>
                </div>
              </>
            ),
          },
          {
            label: 'SPECS',
            component: (
              <div>
                <ul className="grid grid-cols-2 lg:grid-cols-4 p-3 list-disc">
                  {glassFeaturesSorted?.map(feature => {
                    return (
                      <li className="col-span-1 mx-4 my-2" key={feature}>
                        {feature}
                      </li>
                    );
                  })}
                </ul>
              </div>
            ),
          },
        ]}
        value={selectedTabIndex}
        onChange={handleOnTabChange}
      />
    </>
  );
};

export default SpecificationsSection;
